<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import ProgressFrame from './layout/ProgressFrame.vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import { InfoCircleOutlined } from '@ant-design/icons-vue';
import ButtonSecondary from '@/shared/components/button/ButtonSecondary.vue';
import ChevronRightSVG from '@shared/assets/icon/chevron-right.svg';
import AddIcon from '@shared/assets/icon/add.svg';
import InviteCard from '../components/InviteCard.vue';
import { useProjectStore } from '../stores/projectStore';
import { useCommissioningDeviceStore } from '../stores/commissioningDeviceStore';
import type { AssetManagerInvite } from '../models/Project';
import { EmailStatus } from '../models/Project';
import { message } from 'ant-design-vue';
import HorizontalContentRow from '../components/HorizontalContentRow.vue';

const commissioningDeviceStore = useCommissioningDeviceStore();
const serialNumber = commissioningDeviceStore.serialNumber;
const projectStore = useProjectStore();
const router = useRouter();
const invites = ref<AssetManagerInvite[]>([]);

onMounted(() => {
  invites.value = projectStore.assetManagerInvites;
});

const addAssetManager = () => {
  invites.value.push({
    email: '',
  });
};

const removeAssetManager = (index: number) => {
  // Remove at index but do not mutate the array
  const newInvites = [...invites.value];
  newInvites.splice(index, 1);
  invites.value = newInvites;
};

const changeEmail = (index: number, value: string) => {
  const newInvites = [...invites.value];
  newInvites[index].email = value;
  invites.value = newInvites;
};

const sendAssetManagerInvite = async (invite: AssetManagerInvite) => {
  invite.status = EmailStatus.Sending;
  try {
    await projectStore.sendAssetManagerInvite(serialNumber, invite);
    message.success('Invite sent successfully');
  } catch (error) {
    message.error(error ?? 'Failed to send invite, please try again.');
  }
};

const next = () => {
  if (
    invites.value.some((invite) => invite.status !== EmailStatus.Sent && invite.email.trim() !== '')
  ) {
    message.info(
      "You've got some invites waiting to be sent. Please send or delete them before continuing.",
      8, // More duration to allow the user to read the message
    );
    return;
  }

  projectStore.storeAssetManagerInvites(invites.value);
  router.push({ name: 'NPS' });
};
</script>

<template>
  <ProgressFrame pageClass="section-page">
    <div class="content">
      <h2>
        Asset managers
        <a-tooltip overlay-class-name="tooltip-white">
          <template #title>
            <p class="tooltip-content">
              An asset manager could be the Building Manager, or person in charge of this
              installation, who needs to check and review the SolShare data.
            </p>
          </template>
          <InfoCircleOutlined class="info-icon" data-tn="info-icon" />
        </a-tooltip>
      </h2>

      <HorizontalContentRow firstRow lastRow>
        <div class="card-container stack">
          <InviteCard
            v-for="(invite, index) in invites"
            :key="index"
            :id="index"
            :email="invite.email"
            :status="invite.status ?? ''"
            :existingInvites="invites"
            @invite="sendAssetManagerInvite(invite)"
            @remove="removeAssetManager(index)"
            @change="changeEmail(index, $event)"
          />
          <template v-if="invites.length === 0">
            <div class="invite-card-empty" data-tn="invite-card-empty">
              <p>
                Add the Asset Manager's email to send them an invite to review the SolShare data for
                this installation. This step is optional.
              </p>
            </div>
          </template>
        </div>
      </HorizontalContentRow>

      <div class="action-container stack">
        <ButtonSecondary
          block
          class="cta-button"
          @click="addAssetManager"
          data-tn="add-asset-manager-btn"
        >
          <template #iconLeft>
            <AddIcon class="add-icon" />
          </template>
          Add asset manager
        </ButtonSecondary>

        <ButtonPrimary block class="cta-button" @click="next" data-tn="next-btn">
          Next
          <template #iconRight>
            <ChevronRightSVG class="chevron-right-icon" />
          </template>
        </ButtonPrimary>
      </div>
    </div>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.content {
  text-align: center;
  padding: @content-side-margin 0;
}
.info-icon {
  color: @color-secondary;
  font-size: 16px;
  margin-left: 5px;
  position: relative;
  top: -2px;
}

.content-row {
  width: 100%;
}

.card-container {
  width: 100%;
}

.action-container {
  width: 100%;
  margin-top: @margin-top-end-of-page-section;
  padding: 0 @content-side-margin;
}
.stack {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

</style>